/**
 * Generic MQTT adapter — IOT-ADAPTER-LIBRARY-1.
 *
 * Tier: aggregator (protocol-level adapter; works with any device speaking MQTT 3.1.1 / 5.0)
 * Category: asset_tracker
 *
 * The "Switzerland strategy" exemplar: any device emitting telemetry over MQTT
 * — regardless of vendor — can be normalized through this adapter once the
 * tenant configures a topic-pattern → SensorType mapping in their adapter
 * config. This is one of the three "any device, any vendor" anchors for the
 * F-IOT-ADAPTER-001 patent.
 *
 * TYPE-ONLY for this session: capability matrix declared, transport plumbing
 * (broker connection, TLS, ACL) deferred to dedicated integration session.
 *
 * Patent anchor: F-IOT-ADAPTER-001 (hardware-agnostic IoT integration).
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';

const GENERIC_MQTT_CAPABILITIES = {
  configKeys: {
    broker_url: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'MQTT broker URL (mqtt:// or mqtts://); set at adapter provisioning',
    },
    client_id: {
      pushable: false,
      readable: true,
      requiresActivation: true,
      description: 'MQTT client identifier; provisioning-time only',
    },
    qos: {
      pushable: true,
      readable: true,
      enum: ['0', '1', '2'],
      requiresActivation: false,
      description: 'MQTT Quality of Service level',
    },
    topic_subscription: {
      pushable: true,
      readable: true,
      requiresActivation: false,
      description: 'Comma-separated topic patterns the device subscribes to',
    },
    keepalive_sec: {
      pushable: true,
      readable: true,
      range: [10, 65535],
      requiresActivation: false,
      unit: 'seconds',
      description: 'MQTT keepalive interval',
    },
  },
  telemetryTypes: [
    'temperature_c', 'humidity_pct', 'shock_g', 'tilt_deg', 'light_lux',
    'pressure_hpa', 'co2_ppm', 'door_open_bool', 'gps_position', 'speed_kph',
    'battery_voltage_v', 'battery_soc_pct',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 5_000,
};

export class GenericMqttAdapter extends NotImplementedAdapter {
  constructor(device) {
    super(device, 'generic_mqtt', GENERIC_MQTT_CAPABILITIES);
  }
}

export function createGenericMqttAdapterFactory() {
  return (device) => new GenericMqttAdapter(device);
}
